import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, googleProvider } from "./config";
import {
  d1UpsertUserProfile,
  waitForAuthToken,
} from "../services/d1Client";
import {
  clearGuestReadingKeys,
  EVENT_READING_USER_CHANGED,
  setReadingStorageUser,
} from "../bible/readingStorageScope";
import { syncCloudReadingRecords, pushReadingProgressToCloud } from "./readingSyncService";

export const AuthContext = createContext(null);

export const getAuthErrorMessage = (error) => {
  if (!error) return "未知错误";
  const code = error.code || error.message || "";
  switch (code) {
    case "auth/invalid-email":
      return "邮箱格式不正确，请输入有效的邮箱地址";
    case "auth/user-disabled":
      return "该用户账号已被停用，请联系管理员";
    case "auth/user-not-found":
      return "该邮箱未注册，请先注册账号";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "账号或密码错误，请核对后重试";
    case "auth/email-already-in-use":
      return "该邮箱已被注册，请直接登录";
    case "auth/weak-password":
      return "密码强度过弱，建议至少包含 6 位字符";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "登录弹窗已关闭";
    case "auth/configuration-not-found":
      return "Firebase 项目尚未开启 Authentication 身份验证服务，或当前登录提供方未启用。请前往 Firebase 控制台开启对应服务。";
    case "auth/operation-not-allowed":
      return "该登录方式（邮箱或 Google）尚未在 Firebase 控制台开启。请前往控制台启用该登录提供方。";
    case "auth/too-many-requests":
      return "登录尝试过多，系统已暂时锁定，请稍后重试";
    case "auth/network-request-failed":
      return "网络连接失败，请检查网络设置";
    default:
      return error.message || "操作失败，请重试";
  }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readingReady, setReadingReady] = useState(false);

  // Sync user profile in Cloudflare D1
  const syncUserProfile = async (firebaseUser, extraData = {}) => {
    if (!firebaseUser) {
      setUserProfile(null);
      return null;
    }
    try {
      const token = await waitForAuthToken();
      if (!token) {
        throw new Error("Not signed in");
      }
      const profile = await d1UpsertUserProfile(firebaseUser.uid, {
        email: firebaseUser.email || "",
        displayName:
          extraData.displayName ||
          firebaseUser.displayName ||
          (firebaseUser.email ? firebaseUser.email.split("@")[0] : "读者"),
        photoURL: firebaseUser.photoURL || "",
      });
      setUserProfile(profile);
      return profile;
    } catch (err) {
      console.warn("Error syncing user profile to Cloudflare D1:", err);
      // Fallback in-memory profile if network/D1 is offline
      const fallback = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || "",
        displayName:
          extraData.displayName ||
          firebaseUser.displayName ||
          (firebaseUser.email ? firebaseUser.email.split("@")[0] : "读者"),
        photoURL: firebaseUser.photoURL || "",
        role: "user",
        status: "active",
      };
      setUserProfile(fallback);
      return fallback;
    }
  };

  useEffect(() => {
    const onReadingUserChanged = (event) => {
      if (event?.detail?.uid) {
        setReadingReady(false);
      }
    };
    window.addEventListener(EVENT_READING_USER_CHANGED, onReadingUserChanged);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setReadingReady(false);
      setReadingStorageUser(user?.uid);
      setCurrentUser(user);
      if (user) {
        await syncUserProfile(user);
        if (!user.isAnonymous && user.uid !== "local_guest_user") {
          await syncCloudReadingRecords(user.uid);
        }
      } else {
        // Guest reading must survive the initial signed-out auth event and
        // full-page navigations. Account leftovers are already cleared when
        // setReadingStorageUser("") leaves a signed-in session.
        setUserProfile(null);
      }
      setReadingReady(true);
      setLoading(false);
    });

    return () => {
      window.removeEventListener(EVENT_READING_USER_CHANGED, onReadingUserChanged);
      unsubscribe();
    };
  }, []);

  const signInWithEmail = async (email, password) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    await syncUserProfile(credential.user);
    return credential.user;
  };

  const signUpWithEmail = async (email, password, displayName) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      try {
        await updateProfile(credential.user, { displayName });
      } catch (e) {
        console.warn("Failed to update auth profile displayName", e);
      }
    }
    await syncUserProfile(credential.user, { displayName });
    return credential.user;
  };

  const signInWithGoogle = async () => {
    const credential = await signInWithPopup(auth, googleProvider);
    await syncUserProfile(credential.user);
    return credential.user;
  };

  const resetPassword = async (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  const logout = async () => {
    const uid = currentUser?.uid;
    const wasGuest = Boolean(currentUser?.isGuest || uid === "local_guest_user");
    // Drop the header/account user immediately so one click never lands on
    // leftover guest chrome while Firebase sign-out is still settling.
    setCurrentUser(null);
    setUserProfile(null);
    if (!wasGuest && uid) {
      try {
        await pushReadingProgressToCloud(uid);
      } catch (e) {}
    }
    try {
      await signOut(auth);
    } catch (e) {}
    setReadingStorageUser("");
    clearGuestReadingKeys();
    setCurrentUser(null);
    setUserProfile(null);
  };

  const signInAsGuest = async (displayName = "访客读者") => {
    const guestUser = {
      uid: "local_guest_user",
      email: "guest@local.device",
      displayName,
      isAnonymous: true,
      isGuest: true,
    };
    setReadingStorageUser("");
    setCurrentUser(guestUser);
    setUserProfile({
      uid: guestUser.uid,
      email: guestUser.email,
      displayName: guestUser.displayName,
      role: "user",
      status: "active",
      isGuest: true,
    });
    return guestUser;
  };

  const isAdmin = useMemo(() => {
    return userProfile?.role === "admin";
  }, [userProfile]);

  const updateUserProfile = async ({ displayName, photoURL, bio }) => {
    if (!currentUser) return null;

    const nextProfile = {
      displayName,
      photoURL,
      bio,
    };

    if (currentUser.isGuest || currentUser.uid === "local_guest_user") {
      setUserProfile((prev) => ({
        ...prev,
        ...nextProfile,
      }));
      return nextProfile;
    }

    if (auth.currentUser) {
      await updateProfile(auth.currentUser, {
        ...(displayName ? { displayName } : {}),
        ...(photoURL !== undefined ? { photoURL } : {}),
      });
    }

    const updated = await d1UpsertUserProfile(currentUser.uid, {
      displayName,
      photoURL,
      bio,
    });

    setUserProfile((prev) => ({
      ...prev,
      displayName: updated?.displayName || displayName,
      photoURL: updated?.photoURL !== undefined ? updated.photoURL : photoURL,
      bio: updated?.bio !== undefined ? updated.bio : bio,
    }));

    return updated;
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    readingReady,
    isAdmin,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInAsGuest,
    resetPassword,
    logout,
    syncUserProfile,
    updateUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
